import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  useGetKfoFacetsQuery,
  useGetPtoProjectQuery,
  useListKfoReportsQuery,
  type KfoLine,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { KfoGuaranteesTab } from './kfo/KfoGuaranteesTab';
import { KfoImportTab } from './kfo/KfoImportTab';
import { KfoLineDrawer } from './kfo/KfoLineDrawer';
import { KfoRegistryTab } from './kfo/KfoRegistryTab';
import { KfoReportsTab } from './kfo/KfoReportsTab';
import { KfoRfiTab } from './kfo/KfoRfiTab';
import { KfoSummaryTab } from './kfo/KfoSummaryTab';
import { KfoSaveErrorContext } from './kfo/useQtySaver';
import { Banner, Muted, Row, Tab, Tabs, errorText, isNotFound } from './kfo/ui';

type TabKey = 'registry' | 'reports' | 'rfi' | 'go' | 'summary' | 'import';

const TABS: [TabKey, string][] = [
  ['registry', 'Реестр'],
  ['reports', 'Отчёты'],
  ['rfi', 'RFI'],
  ['go', 'ГО'],
  ['summary', 'Сводка'],
  ['import', 'Импорт'],
];

/** Черновик, в который вводится объём в реестре, — запоминаем на проект (удобство). */
const draftKey = (projectId: string) => `osnova.pto.kfo.draft.${projectId}`;

function readDraft(projectId: string): string | null {
  try {
    return window.localStorage.getItem(draftKey(projectId));
  } catch {
    return null;
  }
}

function writeDraft(projectId: string, id: string | null): void {
  try {
    if (id) window.localStorage.setItem(draftKey(projectId), id);
    else window.localStorage.removeItem(draftKey(projectId));
  } catch {
    /* приватный режим / запрет хранилища — просто не запоминаем */
  }
}

/**
 * ОСНОВА.Инженер — «КФО»: контроль физических объёмов Заказчика
 * (docs/pto-kfo-spec.md §8). Срез Заказчика только читается; наш ввод —
 * «Отчёт СМР» / «Приложение к RFI» с позициями и гарантийные письма.
 */
export function PtoKfo() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const tab: TabKey = TABS.some(([k]) => k === rawTab) ? (rawTab as TabKey) : 'registry';

  const setTab = useCallback(
    (t: TabKey) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', t);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const { data: projectData } = useGetPtoProjectQuery(projectId, { skip: !projectId });
  const {
    data: reports,
    isLoading: reportsLoading,
    isSuccess: reportsLoaded,
    isFetching: reportsFetching,
    error: reportsError,
  } = useListKfoReportsQuery(projectId, { skip: !projectId });
  const { error: facetsError } = useGetKfoFacetsQuery(projectId, { skip: !projectId });
  // Бэкенд раздела ещё не развёрнут — оба базовых эндпоинта отвечают 404.
  const unavailable = isNotFound(facetsError) && isNotFound(reportsError);

  const [currentReportId, setCurrentReportIdState] = useState<string | null>(() =>
    readDraft(projectId),
  );
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [openLineId, setOpenLineId] = useState<string | null>(null);
  const [selection, setSelection] = useState<Map<string, KfoLine>>(new Map());
  const [letterOpen, setLetterOpen] = useState(false);
  const [mobileRfiMode, setMobileRfiMode] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 820 : false,
  );
  // Объём, досланный при смене отчёта / уходе с вкладки, не сохранился — экрана
  // ввода уже нет, поэтому сообщаем здесь (useQtySaver → KfoSaveErrorContext).
  const [lostSave, setLostSave] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const onLostSave = useCallback((message: string) => {
    // Ответ пришёл, когда и раздела уже нет, — системное окно, лишь бы не молча.
    if (mounted.current) setLostSave(message);
    else window.alert(message);
  }, []);

  const setCurrentReportId = useCallback(
    (id: string | null) => {
      setCurrentReportIdState(id);
      writeDraft(projectId, id);
    },
    [projectId],
  );

  // Другой проект в том же маршруте — своё состояние.
  useEffect(() => {
    setCurrentReportIdState(readDraft(projectId));
    setSelectedReportId(null);
    setOpenLineId(null);
    setSelection(new Map());
    setLetterOpen(false);
    setLostSave(null);
  }, [projectId]);

  // Запомненный черновик удалён — забываем. Пока список перечитывается, не
  // судим: только что созданного черновика в старом списке ещё нет.
  useEffect(() => {
    if (!reportsLoaded || reportsFetching || !currentReportId) return;
    if (!(reports ?? []).some((r) => r.id === currentReportId)) setCurrentReportId(null);
  }, [reportsLoaded, reportsFetching, reports, currentReportId, setCurrentReportId]);

  const onUseInRegistry = useCallback(
    (id: string | null) => {
      setCurrentReportId(id);
      if (id) setTab('registry');
    },
    [setCurrentReportId, setTab],
  );

  const onOpenReport = useCallback(
    (id: string) => {
      setOpenLineId(null);
      setSelectedReportId(id);
      setTab('reports');
    },
    [setTab],
  );

  const onGuaranteeLetter = useCallback(() => {
    setTab('go');
    setLetterOpen(true);
  }, [setTab]);

  return (
    <KfoSaveErrorContext.Provider value={onLostSave}>
      <div>
        <Row style={{ marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22 }}>КФО — контроль физических объёмов</h1>
            <Muted style={{ marginTop: 4, maxWidth: 820 }}>
              {projectData?.project?.name ? `${projectData.project.name}. ` : ''}
              Срез Заказчика «БД КФО»: строки по Ключу, RFI и гарантийные обязательства. Данные
              Заказчика здесь только читаются; наши «Отчёт СМР» и «Приложение к RFI» выпускаются в
              его формате.
            </Muted>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              type="button"
              $variant={tab === 'rfi' && mobileRfiMode ? 'primary' : 'secondary'}
              $size="sm"
              onClick={() => {
                if (tab !== 'rfi') {
                  setTab('rfi');
                  setMobileRfiMode(true);
                } else {
                  setMobileRfiMode((v) => !v);
                }
              }}
            >
              {tab === 'rfi' && mobileRfiMode
                ? 'Табличный вид RFI'
                : 'Мобильная приёмка RFI (СК/ГК)'}
            </Button>
            <Link to={`/agents/pto/${projectId}`}>
              <Button $variant="secondary">← К проекту</Button>
            </Link>
          </div>
        </Row>

        {unavailable && (
          <Banner>
            Раздел КФО на сервере ещё не развёрнут. Как только его включат, здесь появятся строки
            среза, отчёты и журналы.
          </Banner>
        )}
        {reportsError && !isNotFound(reportsError) ? (
          <Banner $tone="danger">
            {errorText(reportsError, 'Не удалось загрузить отчёты КФО.')}
          </Banner>
        ) : null}
        {lostSave && (
          <Banner $tone="danger">
            <Row style={{ marginBottom: 0 }}>
              <span>{lostSave}</span>
              <Button $size="sm" $variant="ghost" type="button" onClick={() => setLostSave(null)}>
                Скрыть
              </Button>
            </Row>
          </Banner>
        )}

        <Tabs>
          {TABS.map(([k, label]) => (
            <Tab key={k} type="button" $active={tab === k} onClick={() => setTab(k)}>
              {label}
              {k === 'go' && selection.size > 0 ? ` · отмечено ${selection.size}` : ''}
            </Tab>
          ))}
        </Tabs>

        {tab === 'registry' && (
          <KfoRegistryTab
            projectId={projectId}
            canWrite={can_write}
            reports={reports ?? []}
            currentReportId={currentReportId}
            onCurrentReport={setCurrentReportId}
            selection={selection}
            onSelectionChange={setSelection}
            onOpenLine={setOpenLineId}
            onGuaranteeLetter={onGuaranteeLetter}
          />
        )}
        {tab === 'reports' && (
          <KfoReportsTab
            projectId={projectId}
            canWrite={can_write}
            reports={reports ?? []}
            reportsLoading={reportsLoading}
            selectedId={selectedReportId}
            onSelect={setSelectedReportId}
            currentReportId={currentReportId}
            onUseInRegistry={onUseInRegistry}
            onOpenLine={setOpenLineId}
          />
        )}
        {tab === 'rfi' && (
          <KfoRfiTab
            projectId={projectId}
            canWrite={can_write}
            onOpenLine={setOpenLineId}
            mobileMode={mobileRfiMode}
            onToggleMobileMode={() => setMobileRfiMode((v) => !v)}
          />
        )}
        {tab === 'go' && (
          <KfoGuaranteesTab
            projectId={projectId}
            canWrite={can_write}
            selection={selection}
            onClearSelection={() => setSelection(new Map())}
            onGoRegistry={() => setTab('registry')}
            letterOpen={letterOpen}
            onLetterOpen={setLetterOpen}
            onOpenLine={setOpenLineId}
          />
        )}
        {tab === 'summary' && <KfoSummaryTab projectId={projectId} onOpenLine={setOpenLineId} />}
        {tab === 'import' && <KfoImportTab projectId={projectId} canWrite={can_write} />}

        <KfoLineDrawer
          projectId={projectId}
          lineId={openLineId}
          onClose={() => setOpenLineId(null)}
          onOpenReport={onOpenReport}
        />
      </div>
    </KfoSaveErrorContext.Provider>
  );
}
